// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Accordion, ActionIcon, Box, Container, Group, Skeleton, Stack, TextInput } from '@mantine/core';
import { useContext } from 'react';
import { useElementSize, useLocalStorage } from '@mantine/hooks';
import { IconChevronRight, IconSearch, IconX } from '@tabler/icons';
import { i18n } from '@lingui/core';
import { DndProvider } from 'react-dnd-latest';
import { HTML5Backend } from 'react-dnd-html5-backend-latest';
import PositionHeader from '../../../../Molecules/Document/Position/PositionHeader';
import SkeletonItems, { EmptyProductsSkeleton } from '../../../../Atoms/SkeletonItems';
import ProductPosition from '../../../../Molecules/Document/Position/ProductPosition';
import { ProductsContext } from '../../../../Service/Context/proxy/ProductsContext';
import { ProfileContext } from '../../../../Service/Context/ProfileContext';
import BookitupSegmentedControl from '../../../../Atoms/BookitupSegmentedControl';
import { DataS } from '../../../../Service/DataS';
import { TaxType } from '../../../../Service/DocumentT';
import { isDefined } from '../../../../Service/ValidationS';
import LinkText from '../../../../Atoms/LinkText';
import { WindowContext } from '../../../../Service/Context/WindowContext';
import NavAddIcon from '../../../../Templates/Navigation/phone/NavAddIcon';
import { ThemeText } from '../../../../Atoms/ThemeText';
import CategoryProducts from './CategoryProducts';
import { BrowserS } from '../../../../Service/BrowserS';

const ProductsSection = () => {
  const { loading, addingPosition, products, addProduct, categories, searchQuery, setQuery } =
    useContext(ProductsContext);
  const { profile } = useContext(ProfileContext);
  const { sm } = useContext(WindowContext);
  const { ref, width } = useElementSize();
  const [productsTaxType, setProductsTaxType] = useLocalStorage({
    key: 'productsTaxType',
    defaultValue: 'gross',
    getInitialValueInEffect: true,
  });
  const { smallScaleBusiness } = profile ?? {};
  const previewVersion = width < 900;
  const isEmpty = products ? products.length === 0 : true;

  return (
    <Container ref={ref} p={previewVersion ? 0 : undefined}>
      <ThemeText size="lg" weight="bolder" mb="md">
        {i18n._('positions.favourites')}
      </ThemeText>
      <ThemeText>
        Hinterlege hier vordefinierte Produkte, um diese z.B. Deinem Angebot schnell einzufügen.
      </ThemeText>

      <Box
        pt="lg"
        pos={previewVersion ? 'inherit' : 'sticky'}
        sx={(theme) => ({
          width,
          top: 0,
          zIndex: 10,
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : '#FBFBFB',
        })}
      >
        <Group position="apart" mb="md">
          {width > 500 && <LinkText onClick={() => addProduct()}>+ Produkt hinzufügen</LinkText>}
          <TextInput
            value={searchQuery}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder="Produkt suchen"
            icon={<IconSearch size={16} />}
            sx={{ width: 350 }}
            rightSection={
              isDefined(searchQuery) && (
                <ActionIcon size="xs" onClick={() => setQuery('')}>
                  <IconX />
                </ActionIcon>
              )
            }
          />
          {!smallScaleBusiness && (
            <BookitupSegmentedControl
              value={productsTaxType}
              color="blue"
              data={DataS.netGrossOptions}
              radius="md"
              onChange={setProductsTaxType}
              sx={{ minWidth: 160 }}
            />
          )}
        </Group>

        <PositionHeader showDiscount desktopVersion={!previewVersion} documentVersion={false} />
      </Box>
      {addingPosition && <Skeleton mt="sm" width="100%" height={100} />}
      {loading && <SkeletonItems mt="xs" height={100} />}
      {!loading && isEmpty && categories.length === 0 && <EmptyProductsSkeleton onClick={addProduct} />}
      <DndProvider backend={HTML5Backend}>
        <Accordion
          multiple
          variant="separated"
          chevronPosition="left"
          chevron={<IconChevronRight size="1rem" />}
          styles={{
            chevron: {
              '&[data-rotate]': {
                transform: 'rotate(90deg)',
              },
            },
          }}
        >
          <Stack my="sm">
            {categories.map((category) => (
              <CategoryProducts key={category.id} category={category} previewVersion={previewVersion} />
            ))}
          </Stack>
        </Accordion>
      </DndProvider>
      <Stack mt="sm">
        {products
          .filter((p) => p.categoryId === null)
          .map((product) => (
            <ProductPosition
              key={product.id}
              taxType={productsTaxType as TaxType}
              previewVersion={previewVersion}
              position={product}
            />
          ))}
      </Stack>
      {sm && (
        <Box
          pos="fixed"
          sx={{ bottom: BrowserS.runningAsPWA ? 35 : 10, zIndex: 101, left: '50%', transform: 'translatex(-50%)' }}
        >
          <NavAddIcon onClick={() => addProduct()} />
        </Box>
      )}
    </Container>
  );
};

export default ProductsSection;
